// src/entities/asteria.ts — Fase 4: modelo completo con energía, estados, habilidad, vínculo
export type AsteriaElement = 'lumen' | 'aero' | 'terra' | 'aqua' | 'ignis' | 'umbra' | 'astra';
export type AsteriaRarity = 'comun' | 'poco_comun' | 'rara' | 'epica' | 'legendaria';
export type AsteriaBehavior = 'tranquila' | 'curiosa' | 'timida' | 'huidiza' | 'territorial' | 'agresiva' | 'nocturna' | 'misteriosa' | 'estable';
export type AsteriaHabitat = 'hierba' | 'agua' | 'roca' | 'bosque' | 'ruina' | 'claro' | 'puente' | 'cueva';
export type MoveCategory = 'ataque' | 'control' | 'defensa' | 'estado' | 'especial';

export type AsteriaMove = {
  id: string;
  name: string;
  description: string;
  power: number;
  accuracy: number; // 0..1
  energyCost: number; // 0..4
  element: AsteriaElement;
  category: MoveCategory;
  priority: number; // -1..2
  effect?: { statusId?: string; chance?: number; selfBuff?: string; terrainId?: string; weatherId?: string };
};

export type AsteriaSpecies = {
  id: string;
  name: string;
  element: AsteriaElement;
  rarity: AsteriaRarity;
  habitat: AsteriaHabitat[];
  behavior: AsteriaBehavior;
  description: string;
  lore: string;
  baseStats: { hp: number; atk: number; def: number; spd: number; energy?: number };
  captureRate: number;
  moves: AsteriaMove[];
  abilityId: string; // pasiva
  evolution?: { to: string; level: number; note: string };
  color: string;
  accent: string;
  silhouette: 'hoja' | 'chispa' | 'ajolote' | 'draco' | 'polilla' | 'geoda';
  role: 'atacante' | 'defensiva' | 'rapida' | 'soporte' | 'control' | 'equilibrada';
};

export type StatusId = string; // ver data/status.ts

export type AsteriaInstance = {
  uid: string;
  speciesId: string;
  nickname?: string;
  level: number;
  xp: number;
  xpToNext: number;
  iv: number;
  hp: number;
  maxHp: number;
  atk: number;
  def: number;
  spd: number;
  energy: number;
  maxEnergy: number;
  bond: number; // 0..100
  abilityId: string;
  moves: string[]; // ids de movimientos equipados (max 4)
  status?: { id: StatusId; turns: number } | null;
  statStages: { atk: number; def: number; spd: number; acc: number }; // -6..+6
  caughtAt: string;
  caughtZone: string;
  shiny?: boolean;
};

export function calcStats(species: AsteriaSpecies, level: number, iv: number): { hp:number; atk:number; def:number; spd:number; maxHp:number; maxEnergy:number } {
  const f = (base:number) => Math.floor(((base + iv) * 2 * level) / 100) + level + 5;
  const hp = Math.floor(((species.baseStats.hp + iv) * 2 * level) / 100) + level + 10;
  const energyBase = species.baseStats.energy ?? 12;
  const maxEnergy = Math.floor(8 + energyBase * 0.6 + level * 0.4);
  return { hp, maxHp: hp, atk: f(species.baseStats.atk), def: f(species.baseStats.def), spd: f(species.baseStats.spd), maxEnergy };
}

export function createInstance(species: AsteriaSpecies, level: number, zone: string, ivOverride?: number): AsteriaInstance {
  const iv = ivOverride ?? Math.floor(Math.random()*32);
  const st = calcStats(species, level, iv);
  const xpToNext = Math.floor(20 * Math.pow(1.35, level-1));
  return {
    uid: `${species.id}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
    speciesId: species.id,
    level, xp: 0, xpToNext, iv,
    hp: st.hp, maxHp: st.maxHp, atk: st.atk, def: st.def, spd: st.spd,
    energy: st.maxEnergy, maxEnergy: st.maxEnergy,
    bond: 10 + Math.floor(Math.random()*10),
    abilityId: species.abilityId,
    moves: species.moves.slice(0,4).map(m=>m.id),
    status: null,
    statStages: { atk:0, def:0, spd:0, acc:0 },
    caughtAt: new Date().toISOString(),
    caughtZone: zone,
  };
}

export function xpForLevel(level:number): number {
  return Math.floor(20 * Math.pow(1.35, level-1));
}

export type RegistryEntry = { seen: boolean; caught: boolean; seenAt?: string; caughtAt?: string; countSeen:number; countCaught:number };
export type Registry = Record<string, RegistryEntry>;
